import { useCallback, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";

import {
  AlreadyInState,
  ApiError,
  createApiClient,
  Forbidden,
  GuardRefused,
  IdempotencyConflict,
  NotFound,
  RateLimited,
  type RequestOptions,
  Unauthenticated,
  Unavailable,
  Unprocessable,
  VersionConflict,
} from "../api/client";
import { lastActiveAt, markActive } from "../auth/activity";
import { isIdle } from "../auth/idle";
import type { AppConfig } from "../config";

/**
 * The states a write can be in, kept apart because each needs different UI.
 *
 * `guardRefused` in particular is not an error to swallow — the 409 body names
 * what the refusal would break, and showing a generic "failed" throws away the
 * entire point of the guard.
 */
export type WriteState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "done"; response: unknown }
  | { kind: "guardRefused"; error: GuardRefused }
  | { kind: "invalid"; error: Unprocessable }
  | { kind: "denied"; error: Forbidden }
  | { kind: "notfound"; error: NotFound }
  | { kind: "unavailable"; error: Unavailable }
  | { kind: "signedout" }
  /**
   * The session was idle past the limit when this write was attempted, so
   * nothing was sent (S0.2 / DA-3336).
   *
   * Its own kind rather than `signedout`: the operator IS signed in, the
   * write simply was not trusted to be theirs. Collapsing the two would tell
   * someone their session ended when it has not, and send them looking for a
   * sign-in page that will not appear.
   */
  | { kind: "idleReauth" }
  /**
   * 409 `version_conflict` — the document moved since it was read.
   *
   * Carries the version the server holds NOW, so the UI can offer a re-read
   * instead of a dead end. Its own kind rather than `guardRefused`: there is
   * no guard here to force past, and offering `force` would be a lie.
   */
  | { kind: "versionConflict"; error: VersionConflict; currentVersion: number | undefined }
  /**
   * 409 `already_in_state` — nothing to do, and NOT a failure.
   *
   * Reporting this as "refused" sends an operator hunting for a problem that
   * does not exist. The resource already holds what they asked for.
   */
  | { kind: "alreadyInState"; error: AlreadyInState }
  /** 409 `idempotency_conflict` — a key was reused for a different body. */
  | { kind: "idempotencyConflict"; error: IdempotencyConflict }
  /** 429 — the admin API is throttling. The remedy is to wait, not to change the request. */
  | { kind: "rateLimited"; error: RateLimited }
  | { kind: "error"; error: Error };

export interface UseWrite<T> {
  state: WriteState;
  submit: (path: string, init: RequestOptions) => Promise<T | undefined>;
  reset: () => void;
}

/**
 * An `Idempotency-Key` on every mutating request: fresh per submission,
 * reused only for that submission's retries.
 *
 * Labeeb REQUIRES one on each change and answers 400 without it. Replaying
 * the same key with the same body inside 24h returns the first response
 * instead of applying the change twice — which is what makes a retry safe.
 *
 * A RETRY is the same request again after an outcome the server never
 * decided: a 429 from the gateway (labeeb.md §10) or a reply lost to the
 * network, where the change may already have been applied. Both keep the
 * key. Everything else is a new submission with a new key — a second press
 * of Save after a success is a second change, and a version conflict or a
 * guard is an answer the operator must act on, not replay. A different body
 * is never a retry: the same key with another body is `idempotency_conflict`.
 *
 * Left alone when a caller set its own header.
 *
 * GET and HEAD are untouched — the header is meaningless on a read, and
 * adding it would widen the CORS preflight for every fetch in the console.
 */
function withIdempotencyKey(init: RequestOptions, key: string): RequestOptions {
  const headers = new Headers(init.headers);
  if (!headers.has("Idempotency-Key")) headers.set("Idempotency-Key", key);
  return { ...init, headers };
}

function isMutating(init: RequestOptions): boolean {
  const method = (init.method ?? "GET").toUpperCase();
  return method !== "GET" && method !== "HEAD";
}

/** What makes two submits the same request: method, path, query and body. */
function fingerprint(path: string, init: RequestOptions): string {
  return JSON.stringify([
    (init.method ?? "GET").toUpperCase(),
    path,
    init.query ?? null,
    typeof init.body === "string" ? init.body : null,
  ]);
}

/** The server never decided: throttled at the gateway, or no reply at all. */
function isRetryable(err: unknown): boolean {
  return err instanceof RateLimited || !(err instanceof ApiError);
}

export function useWrite<T = unknown>(config: AppConfig): UseWrite<T> {
  const auth = useAuth();
  const [state, setState] = useState<WriteState>({ kind: "idle" });
  const token = auth.user?.id_token;
  // The last submission the server never answered, kept so a retry of it
  // reuses its key. Cleared by any decided outcome.
  const unanswered = useRef<{ fingerprint: string; key: string } | null>(null);

  const submit = useCallback(
    async (path: string, init: RequestOptions): Promise<T | undefined> => {
      // THE IDLE CHECK HAPPENS HERE, before anything is sent, because this
      // hook is the single path every write in the console goes through —
      // putting it on each control would mean the next control added is the
      // one that misses it (S0.2 / DA-3336).
      //
      // Re-authentication is `signinRedirect`, which navigates away and
      // returns with a fresh session. The write is NOT retried on return:
      // an operator who stepped away should see what they are about to do
      // before it happens, not discover it already did.
      if (isIdle(lastActiveAt(), Date.now())) {
        setState({ kind: "idleReauth" });
        void auth.signinRedirect();
        return undefined;
      }
      // Submitting IS activity. Without this the first write after a long
      // read refuses, and the second — moments later — succeeds, which reads
      // as the console being flaky rather than as a rule.
      markActive();

      setState({ kind: "submitting" });
      const api = createApiClient({ apiUrl: config.apiUrl, getIdToken: () => token });
      const print = fingerprint(path, init);
      const pending = unanswered.current;
      const key = pending?.fingerprint === print ? pending.key : crypto.randomUUID();
      unanswered.current = null;
      try {
        const response = await api.request<T>(
          path,
          isMutating(init) ? withIdempotencyKey(init, key) : init,
        );
        setState({ kind: "done", response });
        return response;
      } catch (err: unknown) {
        if (isMutating(init) && isRetryable(err)) unanswered.current = { fingerprint: print, key };
        // ORDER MATTERS. VersionConflict, AlreadyInState and
        // IdempotencyConflict all EXTEND GuardRefused, so a `GuardRefused`
        // check placed first would swallow all three and every Labeeb
        // conflict would render as MCP's forceable guard.
        if (err instanceof VersionConflict)
          setState({ kind: "versionConflict", error: err, currentVersion: err.currentVersion });
        else if (err instanceof AlreadyInState) setState({ kind: "alreadyInState", error: err });
        else if (err instanceof IdempotencyConflict)
          setState({ kind: "idempotencyConflict", error: err });
        else if (err instanceof RateLimited) setState({ kind: "rateLimited", error: err });
        else if (err instanceof GuardRefused) setState({ kind: "guardRefused", error: err });
        else if (err instanceof Unprocessable) setState({ kind: "invalid", error: err });
        else if (err instanceof Forbidden) setState({ kind: "denied", error: err });
        else if (err instanceof NotFound) setState({ kind: "notfound", error: err });
        else if (err instanceof Unavailable) setState({ kind: "unavailable", error: err });
        else if (err instanceof Unauthenticated) setState({ kind: "signedout" });
        else if (err instanceof ApiError) setState({ kind: "error", error: err });
        else if (err instanceof Error) setState({ kind: "error", error: err });
        else setState({ kind: "error", error: new Error(String(err)) });
        return undefined;
      }
    },
    [config.apiUrl, token, auth],
  );

  return { state, submit, reset: useCallback(() => setState({ kind: "idle" }), []) };
}
